import React, { useState, useEffect } from "react";
import "../../assets/css/members.css";
import RowContainer from "../../Components/Common/RowContainer";
import Container from "../../Components/Common/Container";
import Label from "../../Components/Common/Label";
import ColumnContainer from "../../Components/Common/ColumnContainer";
import { useNavigate, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import CustomDropdown from "../../Components/Common/CustomDropdown";
import formatDate from "../../Components/DateFormat";
import VerticalSpace from "../../Components/VerticalSpace";
import Notification from "../../Components/Notifications/Notification";
import "../../stylesheets/indexpage.css";
import PaginationComponent from "../../Components/Common/PaginationComponent";
import { baseURL, getRequest, postRequest } from "../../api/apiClient";
import Toast from "../../Components/Common/Toast";
import DeleteConfirmation from "../../Components/Common/DeleteConfirmation";
import { setDropDown } from "../../redux/lessonsSlice";
import { setParticles } from "../../redux/particlesReducerSlice";
import { useDispatch } from "react-redux";
import Loader from "../../utils/Loading";
import gsap from "gsap";
import "../../stylesheets/common.css";
import ShimmerEffect from "../../utils/ShimmerEffect";
import { useDebounce } from "../../hooks/useDebounce";
import { useResponsiveFlex } from "../../utils/useResponsiveFlex";
import copyIcon from "../../assets/copyIcon.svg";

const InnerRowContainer = styled.div`
  display: flex;
  width: 100%;
  min-width: 1100px;
`;

const ScrollableRowContainer = styled(RowContainer)`
  @media (max-width: 1150px) {
    width: 100%;
    overflow-x: auto;
  }
`;

interface Notifications {
  id: number;
  question: string;
  themeName: string;
  themeIsDeleted?: boolean;
  lessonLevel: string;
  points: string;
  updatedAt: string;
  active: boolean;
}

const NotificationsList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [notifications, setNotifications] = useState<Notifications[]>([]);
  const [selectedItem, setSelectedItem] = useState<string | null>(searchParams.get("filter") || null);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(searchParams.get("level") || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState<number[]>(
    []
  );
  const [selectedNotificationId, setSelectedNotificationId] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "0"));
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(parseInt(searchParams.get("limit") || "5"));
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);

  const getFiltersFromItem = (item: string | null): { level?: string; updatedAt?: number; active?: string } => {
    switch (item) {
      case "Latest update": return { updatedAt: -1 };
      case "Active": return { active: "true" };
      case "InActive": return { active: "false" };
      default: return {};
    }
  };

  const buildParams = (p: number, l: number, s: string, f: string | null, lv: string | null): Record<string, string> => {
    const params: Record<string, string> = { page: p.toString(), limit: l.toString() };
    if (s) params.search = s;
    if (f && f !== "All") params.filter = f;
    if (lv && lv !== "All Levels") params.level = lv;
    return params;
  };

  const [filters, setFilters] = useState<{
    level?: string;
    updatedAt?: number;
    active?: string;
  }>(() => {
    const init = getFiltersFromItem(searchParams.get("filter"));
    const urlLevel = searchParams.get("level");
    if (urlLevel && urlLevel !== "All Levels") init.level = urlLevel;
    return init;
  });
  const flex = useResponsiveFlex();

  const handleSelect = (item: string) => {
    setSelectedItem(item);
  };

  const handleLevelSelect = (item: string) => {
    setSelectedLevel(item);
  };

  const dropdownItems: string[] = [
    "All",
    "Active",
    "InActive",
    "Latest update",
  ];

  const levelDropdownItems: string[] = [
    "All Levels",
    "A1",
    "A2",
    "B1",
    "B2",
  ];

  const debouncedSearch = useDebounce(searchQuery, 500);

  // Sync state when browser Back/Forward changes the URL
  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page") || "0");
    const urlLimit = parseInt(searchParams.get("limit") || "5");
    const urlSearch = searchParams.get("search") || "";
    const urlFilter = searchParams.get("filter") || null;
    const urlLevel = searchParams.get("level") || null;
    setPage(urlPage);
    setLimit(urlLimit);
    setSearchQuery(urlSearch);
    setSelectedItem(urlFilter);
    setSelectedLevel(urlLevel);
    const synced = getFiltersFromItem(urlFilter);
    if (urlLevel && urlLevel !== "All Levels") synced.level = urlLevel;
    setFilters(synced);
  }, [searchParams]);

  useEffect(() => {
    const updatedFilters = {
      ...filters,
      search: debouncedSearch,
    };
    fetchNotifications(page + 1, limit, updatedFilters);
  }, [debouncedSearch, page, limit, filters]);

  //fetch notification list
  const fetchNotifications = async (
    page: number,
    perPage: number,
    filters: {
      level?: string;
      updatedAt?: number;
      active?: string;
      search?: string;
    }
  ) => {
    if (!filters) {
      setLoading(true);
    } else {
      setFilterLoading(true);
    }

    try {
      const url = `${baseURL}/notification-challenge/get-all-notification-challenge`;
      const body = {
        page,
        limit: perPage,
        ...filters,
      };
      const response = await postRequest(url, body);
      setLoading(false);
      setFilterLoading(false);
      if (response?.data) {
        setNotifications(response.data.data.result);
        setCount(response.data.data.totalChallengesCount);
        setPage(response.data.data.page - 1);
        setHasNextPage(response.data.data.hasNextPage);
        setHasPreviousPage(response.data.data.hasPreviousPage);
      }

      const fetchLessons = await getRequest(
        `${baseURL}/notification-challenge/get-lesson-dropdown`
      );

      const fetchParticles = await getRequest(
        `${baseURL}/particle/get-dropdown`
      );
      dispatch(setDropDown(fetchLessons?.data?.data || []));
      dispatch(setParticles(fetchParticles?.data?.data || []));
    } catch (error) {
      console.error("Error fetching lessons:", error);
    }
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(0);
    setSearchParams(buildParams(0, limit, query, selectedItem, selectedLevel));
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    setSearchParams(buildParams(newPage, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    setSearchParams(buildParams(0, newLimit, searchQuery, selectedItem, selectedLevel));
  };

  const handleApplyFilters = () => {
    const newFilters = getFiltersFromItem(selectedItem);
    if (selectedLevel && selectedLevel !== "All Levels") {
      newFilters.level = selectedLevel;
    }
    setFilters(newFilters);
    setPage(0);
    setSearchParams(buildParams(0, limit, searchQuery, selectedItem, selectedLevel));
  };

  const handleCopyNotification = async (id: number) => {
    try {
      const response = await postRequest(
        `${baseURL}/notification-challenge/clone-notification-challenge`,
        { id }
      );
      if (response?.data.success) {
        Toast(response.data.statusMessage || "Notification cloned successfully", "success");
        const updatedFilters = { ...filters, search: debouncedSearch };
        fetchNotifications(page + 1, limit, updatedFilters);
      } else {
        Toast("Failed to clone notification", "error");
      }
    } catch (error) {
      console.error("Error cloning notification:", error);
      Toast("Failed to clone notification", "error");
    }
  };

  /** Bulk copy all selected notifications — clones each one server-side */
  const handleCopySelectedNotifications = async () => {
    if (selectedNotifications.length === 0) return;
    try {
      for (const id of selectedNotifications) {
        await postRequest(
          `${baseURL}/notification-challenge/clone-notification-challenge`,
          { id }
        );
      }
      Toast(`${selectedNotifications.length} notification(s) copied successfully`, "success");
      setSelectedNotifications([]);
      const updatedFilters = { ...filters, search: debouncedSearch };
      fetchNotifications(page + 1, limit, updatedFilters);
    } catch (error) {
      console.error("Error copying notifications:", error);
      Toast("Failed to copy notifications", "error");
    }
  };

  const handleEditNotification = (id: number) => {
    navigate(`/notifications/edit-notification/${id}`, {
      state: { listPage: page, listLimit: limit, listSearch: searchQuery, listFilter: selectedItem, listLevel: selectedLevel },
    });
  };

  const handleCreateNotification = () => {
    navigate("/notifications/create-notification");
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const reqData = {
      modelName: "notificationChallenge",
      id: id,
      isActive: !active,
    };
    await postRequest(`${baseURL}/change-status`, reqData);
    // await fetchNotifications(page + 1, limit, filters);
  };

  const onCheckboxChange = (id: number) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(
        selectedNotifications.filter((selectedId) => selectedId !== id)
      );
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const deleteSelectedNotifications = () => {
    if (selectedNotifications.length > 0) {
      setShowDeleteDialog(true);
    } else {
      Toast("Please select at least one notifications to delete.", "error");
    }
  };

  const deleteNotifications = async (notificationIds: number | number[]) => {
    try {
      const payload = {
        id: Array.isArray(notificationIds) ? notificationIds : notificationIds,
      };
      const response = await postRequest(
        `${baseURL}/notification-challenge/delete-notification-challenge-by-id`,
        payload
      );

      if (response?.status === 200) {
        Toast("Notifications deleted successfully", "success");
        setNotifications((prevData) =>
          prevData.filter((notification) =>
            Array.isArray(notificationIds)
              ? !notificationIds.includes(notification.id)
              : notification.id !== notificationIds
          )
        );
      } else {
        Toast("Failed to delete notifications", "error");
      }
    } catch (error) {
      console.error("Error deleting notifications:", error);
    }
  };

  const handleDelete = async () => {
    if (selectedNotificationId !== null) {
      await deleteNotifications(selectedNotificationId);
    } else {
      await deleteNotifications(selectedNotifications);
    }
    // await fetchNotifications(page + 1, limit, filters);
    setSelectedNotifications([]);
    setSelectedNotificationId(null);
    setShowDeleteDialog(false);
  };

  const toggleDeleteDialog = () => {
    setShowDeleteDialog(!showDeleteDialog);
  };

  useEffect(() => {
    let timeLine = gsap.timeline();
    timeLine.from(".row-container", {
      opacity: 0,
      y: -40,
      duration: 0.3,
      ease: "power2.out",
    });

    timeLine.from(".user-data", {
      opacity: 0,
      y: 40,
      duration: 0.5,
      ease: "power2.out",
      stagger: 0.2,
    });
  }, [loading]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <div className="container-fluid">
            <div className="p-3">
              <div className="row mb-3 ">
                <div className="col">
                  <h2 style={{ fontWeight: "bold" }}>
                    Challenge: Notificaciones
                  </h2>
                </div>
              </div>

              {/* Filter Label */}
              <div className="row mb-3">
                <div className="col">
                  <label htmlFor="filter" className="form-label">
                    Filter
                  </label>
                </div>
              </div>

              {/* Buttons Row */}
              <div className="row mb-3 all-button-responsive">
                <div className="col-3 search-input-div">
                  <input
                    type="search"
                    className="form-control rounded"
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    aria-label="Search"
                    aria-describedby="search-addon"
                    style={{
                      height: "100%",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <div className="col d-flex responsive">
                  <CustomDropdown
                    items={dropdownItems}
                    onSelect={handleSelect}
                    selectedItem={selectedItem || ""}
                    height="50px"
                  />
                  <div className="ms-3">
                    <CustomDropdown
                      items={levelDropdownItems}
                      onSelect={handleLevelSelect}
                      selectedItem={selectedLevel || ""}
                      height="50px"
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary ms-3 user"
                    style={{
                      backgroundColor: "#100F15",
                      borderRadius: "10px",
                      color: "#FFFFFF",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 50px",
                    }}
                    onClick={handleApplyFilters}
                  >
                    Apply
                  </button>
                </div>
                <div className="col d-flex justify-content-end responsive">
                  {/* <button
                    type="button"
                    className="btn btn-outline-secondary me-2 text-center"
                    style={{
                      color: "#100F15",
                      background: "none",
                      border: "1px solid #100F15",
                      borderRadius: "10px",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={deleteSelectedNotifications}
                  >
                    Delete all
                  </button> */}
                  <button
                    type="button"
                    className="btn btn-success user"
                    style={{
                      backgroundColor: "#FF9E00",
                      borderRadius: "10px",
                      color: "#100F15",
                      fontSize: "14px",
                      width: "auto",
                      padding: "14px 40px",
                    }}
                    onClick={handleCreateNotification}
                  >
                    Add New
                  </button>
                </div>
              </div>

              <ScrollableRowContainer>
                <InnerRowContainer>
                  <Container width="100%">
                    <RowContainer
                      alignItem="center"
                      padding="8px 12px 8px 12px"
                      gap="8px"
                      justifyContent="space-between"
                      className="row-container"
                    >
                      {/* Select-all checkbox for current page */}
                      <RowContainer flex="1.4" alignItem="center">
                        <input
                          type="checkbox"
                          title="Select all on this page"
                          checked={
                            notifications.length > 0 &&
                            notifications.every((n) =>
                              selectedNotifications.includes(n.id)
                            )
                          }
                          ref={(el) => {
                            if (el) {
                              el.indeterminate =
                                selectedNotifications.length > 0 &&
                                !notifications.every((n) =>
                                  selectedNotifications.includes(n.id)
                                );
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              // select all on current page
                              const currentPageIds = notifications.map((n) => n.id);
                              setSelectedNotifications((prev) => [
                                ...prev.filter((id) => !currentPageIds.includes(id)),
                                ...currentPageIds,
                              ]);
                            } else {
                              // deselect all on current page
                              const currentPageIds = notifications.map((n) => n.id);
                              setSelectedNotifications((prev) =>
                                prev.filter((id) => !currentPageIds.includes(id))
                              );
                            }
                          }}
                          style={{
                            width: 16,
                            height: 16,
                            accentColor: "#7542E4",
                            cursor: "pointer",
                          }}
                        />
                      </RowContainer>
                      <RowContainer flex={flex.name}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Name
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.theme}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Theme
                        </Label>
                      </RowContainer>
                      <RowContainer flex="3.1">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Level
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.point}>
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Point
                        </Label>
                      </RowContainer>
                      <RowContainer flex="5">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Latest updated
                        </Label>
                      </RowContainer>
                      <RowContainer flex={flex.action} justifyContent="space-between" alignItem="center">
                        <Label
                          fontWeight={400}
                          fontSize="14px"
                          color="rgba(164, 163, 167, 1)"
                        >
                          Action
                        </Label>
                        {/* Bulk Copy toolbar — visible only when notifications are selected */}
                        {selectedNotifications.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <button
                              type="button"
                              className="btn user"
                              title={`Copy ${selectedNotifications.length} selected notification(s)`}
                              style={{
                                backgroundColor: "#DAD7FE",
                                color: "#000",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 12px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                                whiteSpace: "nowrap",
                              }}
                              onClick={handleCopySelectedNotifications}
                            >
                              <img
                                src={copyIcon}
                                alt="copy"
                                style={{ width: 16, height: 16 }}
                              />
                              Copy ({selectedNotifications.length})
                            </button>
                            <button
                              type="button"
                              className="btn user"
                              title="Clear selection"
                              style={{
                                background: "none",
                                border: "1px solid #aaa",
                                borderRadius: 8,
                                fontSize: 12,
                                padding: "5px 10px",
                                color: "#555",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() => setSelectedNotifications([])}
                            >
                              ✕ Clear
                            </button>
                          </div>
                        )}
                      </RowContainer>
                    </RowContainer>
                    {filterLoading ? (
                      <>
                        <RowContainer
                          bgColor="#ffffff"
                          borderRadius="8px"
                          padding="10px 0px"
                        >
                          {Array.from({ length: 6 }, (_, index) => {
                            return (
                              <RowContainer
                                key={index}
                                width={
                                  index === 0 || index === 5 ? "10%" : "20%"
                                }
                                justifyContent="center"
                              >
                                <ShimmerEffect />
                              </RowContainer>
                            );
                          })}
                        </RowContainer>
                      </>
                    ) : (
                      <>
                        {notifications && notifications.length > 0 ? (
                          <ColumnContainer>
                            {notifications.map((notification) => (
                              <Notification
                                key={notification.id}
                                id={notification.id}
                                name={notification.question}
                                theme={notification.themeName}
                                themeIsDeleted={notification.themeIsDeleted}
                                lessonLevel={notification.lessonLevel}
                                point={`${notification.points} pts`}
                                created={formatDate(notification.updatedAt)}
                                onEdit={handleEditNotification}
                                onDelete={() => {
                                  setSelectedNotificationId(notification.id);
                                  toggleDeleteDialog();
                                }}
                                isChecked={selectedNotifications.includes(
                                  notification.id
                                )}
                                onCheckboxChange={onCheckboxChange}
                                status={notification.active}
                                onStatusChange={() =>
                                  handleStatusChange(
                                    notification.id,
                                    notification.active
                                  )
                                }
                                onCopy={handleCopyNotification}
                              />
                            ))}
                          </ColumnContainer>
                        ) : (
                          <>
                            <VerticalSpace height={30} />
                            <Label
                              alignItems="center"
                              display="flex"
                              color="rgba(164, 163, 167, 1)"
                              justifyContent="center"
                            >
                              No results found.
                            </Label>
                          </>
                        )}
                      </>
                    )}
                  </Container>
                </InnerRowContainer>
              </ScrollableRowContainer>
              <VerticalSpace height={10} />
              <RowContainer justifyContent="flex-end" className="user-data">
                <PaginationComponent
                  page={page}
                  rowsPerPage={limit}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  count={count}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                />
              </RowContainer>
            </div>
            {showDeleteDialog && (
              <DeleteConfirmation
                showDeletePopup={true}
                setShowDeletePopup={setShowDeleteDialog}
                message={`Are you sure you want to delete ${selectedNotifications.length === 0
                  ? "this notification?"
                  : selectedNotifications.length === 1
                    ? "this notifications?"
                    : "these notifications?"
                  }`}
                deleteButtonLabel="Delete"
                onDelete={handleDelete}
              />
            )}
            <VerticalSpace height={100} />
          </div>
        </>
      )}
    </>
  );
};

export default NotificationsList;
